import { ConditionalKeyOf, OptionalFunction } from "@hex/common";
// eslint-disable-next-line tree-shaking/no-side-effects-in-initialization
import memoize from "micro-memoize";
import React, {
  FocusEventHandler,
  FormEventHandler,
  KeyboardEventHandler,
  MouseEventHandler,
  MutableRefObject,
  RefObject,
  createRef,
} from "react";
import styled from "styled-components";

import { shallowEqual } from "../../util/index.js";
import { DeleteKeys, Keys, ModKey } from "../../util/Keys";

const ContentContainer = styled.span<{ isEditing: boolean }>`
  ${({ isEditing }) => (!isEditing ? "box-shadow: none;" : "")}
  min-width: 0;

  overflow: hidden;

  line-height: 14px;
  white-space: nowrap;

  text-overflow: ellipsis;

  ${({ isEditing }) => isEditing && `flex: none;`}
`;

interface ControlledContentEditableProps {
  id?: string;
  "data-test"?: string;
  className?: string;
  content: string;
  isEditing: boolean;
  maxLength?: number;
  selectAllWhenEditingStarts?: boolean;
  spellCheck?: boolean;
  onChange: (newValue: string) => void;
  onCancel: () => void;
  onSave: (newValue: string) => void;
  onClick?: MouseEventHandler<HTMLSpanElement>;
  onBlur?: FocusEventHandler<HTMLSpanElement>;
  onFocus?: FocusEventHandler<HTMLSpanElement>;
  /**
   * @deprecated
   * This doesn't actually fire on key press, but don't want to remove it and break usage
   */
  onKeyPressDeprecated?: KeyboardEventHandler<HTMLSpanElement>;
  onKeyDown?: KeyboardEventHandler<HTMLSpanElement>;
  // Note when adding more callbacks here: they will get automatically skipped
  // in componentShouldUpdate - be sure to make a method below that calls the prop function
  // and pass that in to the ContentEditable, otherwise it will be out of date
}

/**
 * ContentEditable has nice properties (like growing to fit the text) but really hates to be controlled.
 * This needs to be a class so we can block updates when the props content matches the current content,
 * otherwise the cursor gets reset. We also need to manipulate the ref directly to force the
 * contenteditable div to update.
 */
export class ControlledContentEditable extends React.Component<ControlledContentEditableProps> {
  private contentRef: RefObject<HTMLDivElement>;
  private lastClickedRef: MutableRefObject<React.MouseEvent<
    HTMLSpanElement,
    MouseEvent
  > | null>;

  constructor(props: ControlledContentEditableProps) {
    super(props);
    this.contentRef = createRef();
    this.lastClickedRef = createRef();
  }

  override shouldComponentUpdate(
    nextProps: ControlledContentEditableProps,
  ): boolean {
    /**
     * This object selects the keys that we want to skip when comparing prop equality.
     * Since it's super easy for functions to change (and thus reset the cursor), we
     * want them skipped by default. This type accomplishes that by asserting that all
     * keys of type function in ControlledContentEditableProps must be assigned the value
     * true, which we then use to filter.
     */
    const shouldSkip: Record<keyof ControlledContentEditableProps, boolean> &
      Record<
        Exclude<
          ConditionalKeyOf<ControlledContentEditableProps, OptionalFunction>,
          undefined
        >,
        true
      > = {
      id: false,
      "data-test": false,
      className: false,
      // Note: we want to skip content for this check because we handle it further down
      content: true,
      isEditing: false,
      maxLength: true,
      // We can skip this because we don't need the component to do anything if only this prop changes
      selectAllWhenEditingStarts: true,
      spellCheck: false,
      onChange: true,
      onCancel: true,
      onSave: true,
      onClick: true,
      onBlur: true,
      onFocus: true,
      onKeyPressDeprecated: true,
      onKeyDown: true,
    };

    const keysToSkip = new Set(
      Object.keys(shouldSkip).filter(
        (key) => shouldSkip[key as keyof ControlledContentEditableProps],
      ),
    );

    const propsEqual = shallowEqual(this.props, nextProps, {
      ignoreKeys: keysToSkip,
    });

    if (!propsEqual) {
      return true;
    }

    if (nextProps.content !== this.contentRef.current?.textContent) {
      return true;
    }

    return false;
  }

  override componentDidMount(): void {
    if (this.contentRef.current != null) {
      this.contentRef.current.textContent = this.props.content;

      if (this.props.isEditing) {
        this.contentRef.current.focus();

        if (this.props.selectAllWhenEditingStarts) {
          this.selectAllText();
        }
      }
    }
  }

  caretPositionFromPoint(
    x: number,
    y: number,
  ): {
    offsetNode: Node;
    offset: number;
  } | null {
    if ("caretPositionFromPoint" in document) {
      // Official API supported by Chrome, Firefox, Edge as of 2024
      return document.caretPositionFromPoint(x, y);
    } else {
      // without `caretPositionFromPoint`, we don't have a lot of resolution
      // on where to place the cursor, so we only either place selection at
      // the start or end of the text. We favor the end of the text (which is
      // more conducive to both appending and to quickly deleting everything)
      // unless the click is very close to the start of the text bounds.
      const focusTarget =
        this.contentRef.current?.firstChild ?? this.contentRef.current;
      if (!focusTarget) return null;
      const bounds = this.contentRef.current?.getBoundingClientRect() ?? {
        left: 0,
        width: 0,
      };
      const selectionIsAtStart = x < bounds.left + bounds.width / 8;
      const textLength = (this.contentRef.current?.textContent ?? "").length;
      return {
        offsetNode: focusTarget,
        offset: selectionIsAtStart ? 0 : textLength,
      };
    }
  }

  override componentDidUpdate(prevProps: ControlledContentEditableProps): void {
    if (this.contentRef.current != null) {
      this.contentRef.current.textContent = this.props.content;

      if (this.props.isEditing && !prevProps.isEditing) {
        this.contentRef.current.focus();
        const lastClicked = this.lastClickedRef.current;
        this.lastClickedRef.current = null;
        if (this.props.selectAllWhenEditingStarts) {
          this.selectAllText();
        } else if (lastClicked) {
          // retain the cursor position if the user clicked into this
          // node to begin editing it, so the cursor doesn't skip to the back
          // of the text when focused
          const docSelection = document.getSelection();
          const target = this.contentRef.current.firstChild;
          if (docSelection && target) {
            docSelection.removeAllRanges();
            const point = this.caretPositionFromPoint(
              lastClicked.pageX,
              lastClicked.pageY,
            );
            if (point) docSelection.setPosition(point.offsetNode, point.offset);
          }
        }
      }
    }
  }

  selectAllText = (): void => {
    this.contentRef.current?.focus();
    const selection = document.getSelection();
    if (selection != null && this.contentRef.current != null) {
      selection.removeAllRanges();
      selection.selectAllChildren(this.contentRef.current);
    }
  };

  keyDownHandler: KeyboardEventHandler<HTMLSpanElement> = (evt): void => {
    const maxLength = this.props.maxLength;
    const currentValue = evt.currentTarget.textContent || "";
    this.props.onKeyDown?.(evt);

    // do not allow the key event to trigger the field to be modified if the max has been reached.
    if (
      maxLength &&
      maxLength <= currentValue.length &&
      !DeleteKeys.includes(evt.key)
    ) {
      evt.preventDefault();
      return;
    }

    // Make sure we arn't stopping propagation of modifier keys
    if (evt.key !== Keys.SHIFT && evt.key !== ModKey) {
      evt.stopPropagation();
    }

    if (evt.key === Keys.ENTER) {
      this.props.onKeyPressDeprecated?.(evt);
      // Swallow newlines
      evt.preventDefault();
    }
  };

  keyPressHandler: KeyboardEventHandler<HTMLSpanElement> = (evt): void => {
    // Make sure we arn't stopping propagation of modifier keys
    if (evt.key !== Keys.SHIFT && evt.key !== ModKey) {
      evt.stopPropagation();
    }
  };

  keyUpHandler: KeyboardEventHandler<HTMLSpanElement> = (evt): void => {
    // Make sure we arn't stopping propagation of modifier keys
    if (evt.key !== Keys.SHIFT && evt.key !== ModKey) {
      evt.stopPropagation();
    }

    const { maxLength, onCancel, onChange, onSave } = this.props;
    const currentValue = evt.currentTarget.textContent || "";
    if (evt.key === Keys.ENTER) {
      // Enter
      // only allow the change to pass through if the content is under maxLength
      if (!maxLength || maxLength >= currentValue.length) {
        onChange(evt.currentTarget.textContent || "");
        onSave(currentValue);
      } else {
        onCancel();
      }
    } else if (evt.key === Keys.ESCAPE) {
      // Escape
      onCancel();
    }
  };

  onInput: FormEventHandler<HTMLSpanElement> = (evt): void => {
    const { onChange } = this.props;

    const currentValue = evt.currentTarget.textContent || "";
    onChange(currentValue);
  };

  // These are reference stable wrappers around some functions that get passed in
  // so we don't need to rerender the content container to get prop updates
  onBlur: FocusEventHandler<HTMLSpanElement> = (evt): void => {
    // Blur is called when we click out of the HTML element
    // If Blur is called when we are over the word limit, discard the most recent changes
    const { maxLength, onCancel } = this.props;
    if (maxLength && maxLength < this.props.content.length) {
      onCancel();
    }
    this.props.onBlur?.(evt);
  };

  onClick: MouseEventHandler<HTMLSpanElement> = (evt): void => {
    // retain the last clicked event in case we need to set the cursor
    // position when the content becomes focused and editable
    evt.persist();
    this.lastClickedRef.current = evt;
    this.props.onClick?.(evt);
  };

  onFocus: FocusEventHandler<HTMLSpanElement> = (evt): void => {
    this.props.onFocus?.(evt);
  };

  override render(): JSX.Element {
    const {
      className,
      content,
      "data-test": dataTest,
      id,
      isEditing,
      spellCheck = false,
    } = this.props;
    return (
      <ContentContainer
        ref={this.contentRef}
        className={className}
        contentEditable={
          isEditing
            ? supportsPlainText()
              ? // Without this, chrome will allow rich text shortcuts such as cmd + b
                // https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/contenteditable#value
                ("plaintext-only" as unknown as true)
              : true
            : false
        }
        data-test={dataTest}
        id={id}
        isEditing={isEditing}
        spellCheck={spellCheck}
        suppressContentEditableWarning={true}
        tabIndex={0}
        title={content}
        onBlur={this.onBlur}
        onClick={this.onClick}
        onFocus={this.onFocus}
        onInput={this.onInput}
        onKeyDown={this.keyDownHandler}
        onKeyPress={this.keyPressHandler}
        onKeyUp={this.keyUpHandler}
      />
    );
  }
}

// Can be removed once a reasonable percentage of clients are on firefox 136 or higher
// https://app.datadoghq.com/rum/sessions?query=%40type%3Asession%20%40application.id%3Ad2847f2e-e35a-42d8-9821-8be97b586295%20%40browser.name%3AFirefox&agg_m=count&agg_m_source=base&agg_q=%40browser.version&agg_q_source=base&agg_t=count&cols=&fromUser=false&top_n=10&top_o=top&track=rum&viz=sunburst&x_missing=true&from_ts=1756856946206&to_ts=1756943346206&live=true
const supportsPlainText = memoize((): boolean => {
  const d = document.createElement("div");
  try {
    d.contentEditable = "plaintext-only";
    const result = d.contentEditable === "plaintext-only";
    return result;
    // eslint-disable-next-line base/no-restricted-syntax -- feature detection
  } catch {
    return false;
  } finally {
    d.remove();
  }
});
